import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

/* ── Default hardcoded catering data ─────────────────────────────────────── */
const DEFAULT_STARTERS = {
  title: 'Starters', icon: '🍢',
  groups: [
    {
      label: 'VEG', color: '#16a34a',
      items: [
        ['Gobi Manchurian', 85, 160], ['Chilli Gobi', 85, 160], ['Paneer 65', 85, 160],
        ['Chilli Paneer', 85, 160], ['Veg Manchurian', 95, 180], ['Baby Corn Chilli', 85, 160],
        ['Gobi 555', 85, 160], ['Baby Corn Pepper', 85, 160], ['Paneer Manchurian', 85, 160],
        ['Gobi 65', 85, 160], ['Chilli Babycorn', 85, 160], ['Babycorn Manchurian', 85, 160],
        ['Babycorn 65', 85, 160],
      ],
    },
    {
      label: 'NON-VEG', color: '#dc2626',
      items: [
        ['Chicken 65', 95, 180], ['Chicken 555', 95, 180], ['Chilli Chicken', 95, 180],
        ['Pepper Chicken', 95, 180], ['Apollo Fish', 100, 200], ['Karampodi Chicken', 100, 200],
        ['Karivepaku Chicken', 100, 200], ['Shrimp 555', 95, 180], ['Shrimp 65', 95, 180],
        ['Chilli Shrimp', 95, 180], ['Chicken Manchurian', 95, 180],
      ],
    },
  ],
};

const DEFAULT_BIRYANIS = {
  title: 'Biryanis & Rice', icon: '🍚',
  groups: [
    {
      label: 'VEG', color: '#16a34a',
      items: [
        ['Veg Dum Biryani', 100, 160], ['Paneer Biryani', 85, 160], ['White Rice', '—', 50],
        ['Pulao Rice', 65, 120], ['Veg Pulao', 80, 150], ['Paneer Pulao', 85, 160],
        ['Bagara Rice', 65, 120], ['Coconut Rice', 75, 140], ['Veg Biryani', 75, 150],
        ['Veg Kheema Biryani/Pulav', 95, 180], ['Gutti Vankaya Biryani/Pulav', 85, 170],
      ],
    },
    { label: 'EGG', color: '#ca8a04', items: [['Egg Pulao/Biryani', 80, 160]] },
    {
      label: 'CHICKEN', color: '#ea580c',
      items: [
        ['Vijayawada Boneless Biryani', 95, 180], ['Raju Gari Kodi Biryani', 100, 200],
        ['DB Special Boneless Biryani', 95, 180], ['Chicken Dum Biryani/Pulav', 85, 170],
        ['Gongura Chicken Biryani/Pulav', 100, 200], ['Avakaya Chicken Biryani/Pulav', 100, 200],
        ['Monagadu Biryani/Pulav', 100, 200],
      ],
    },
    {
      label: 'GOAT', color: '#92400e',
      items: [
        ['Mutton Dum Biryani', 120, 230], ['Goat Kheema Biryani/Pulav', 120, 230],
        ['Gongura Mutton Biryani/Pulav', 130, 250], ['Andhra Goat Biryani/Pulav', 120, 230],
        ['Telangana Goat Biryani/Pulav', 120, 230],
      ],
    },
    { label: 'SEAFOOD', color: '#0891b2', items: [['Shrimp Biryani/Pulav', 95, 180]] },
  ],
};

const DEFAULT_CURRIES = {
  title: 'Curries', icon: '🍛',
  groups: [
    {
      label: 'VEG', color: '#16a34a',
      items: [
        ['Paneer Butter Masala', 85, 160], ['Kadai Paneer', 85, 160], ['Mixed Veg Curry', 85, 160],
        ['Veg Kurma', 85, 160], ['Gutti Vankaya Masala', 85, 160], ['Chole Masala', 85, 160],
        ['Sambar', 65, 120], ['Rasam', 65, 120], ['Palak Dal', 75, 140], ['Tomato Dal', 75, 140],
        ['Cashew Paneer Curry', 95, 180], ['Paneer Tikka Masala', 85, 160],
        ['Methi Paneer', 90, 170], ['Pool Makana', 90, 170], ['Malai Paneer', 80, 160],
        ['Paneer Do Pyaza', 80, 160], ['Mushroom Do Pyaza', 80, 160], ['Kadai Mushroom', 80, 160],
        ['Okra Fry', 110, 200], ['Tindora Fry', 110, 200], ['Cabbage Fry', 75, 150],
        ['Cashew Drumstick', 110, 200], ['Mutter Paneer', 85, 160], ['Aloo Mutter', 75, 140],
        ['Dal Makhni', 85, 160], ['Yellow Dal Tadka', 65, 120], ['Navaratan Koorma', 85, 160],
        ['Soya Nihari', 85, 160], ['Soya Cashew Curry', 85, 170], ['Saag Paneer', 95, 180],
      ],
    },
    { label: 'EGG', color: '#ca8a04', items: [['Egg Malai', 85, 160]] },
    {
      label: 'NON-VEG', color: '#dc2626',
      items: [
        ['Chicken Curry', 85, 180], ['Kadai Chicken', 95, 180], ['Butter Chicken', 95, 180],
        ['Chicken Mugalai', 110, 200], ['Chicken Butter Masala', 85, 160],
        ['Chicken Tikka Masala', 90, 170], ['Saag Chicken', 110, 200],
        ['Gongura Chicken Curry', 110, 200], ['Shrimp Curry', 95, 180],
      ],
    },
    {
      label: 'GOAT', color: '#92400e',
      items: [['Goat Curry', 130, 250], ['Mugalai Goat', 150, 280], ['Saag Mutton', 150, 280]],
    },
  ],
};

const DEFAULT_DATA = {
  starters: DEFAULT_STARTERS,
  biryanis: DEFAULT_BIRYANIS,
  curries: DEFAULT_CURRIES,
  breakfast: [
    ['Idly', '>30', '$2/ea'], ['Poori', '>30', '$2/ea'], ['Vada', '>30', '$2/ea'],
    ['Garellu', '>30', '$2/ea'], ['Masala Vada', '>30', '$2/ea'],
    ['Mysore Bonda', '>30', '$1.6/ea'], ['Punugulu', '>40', '$0.8/ea'],
  ],
  snacksPiece: [
    ['Potato Samosa', '>40', '$1.5/ea'],
    ['Mirchi Bajji', '>40', '$1.75/ea'],
    ['Onion Stuffed Mirchi', '>30', '$2/ea'],
  ],
  snacksTray: [
    ['Onion Pakodi', '$60', '$110'],
    ['Spinach Pakodi', '$65', '$120'],
    ['Cut Mirchi', '$65', '$120'],
  ],
  drinksDesserts: [
    ['Tea', '>1 Gal', '$35'], ['Coffee', '>1 Gal', '$40'], ['Mango Lassi', '>1 Gal', '$50'],
    ['Pista Kulfi', '>50', '$2.5/ea'], ['Mango Kulfi', '>50', '$2.5/ea'],
    ['Gulab Jamun', '—', '$1/ea'], ['Rava Kesari', '$75', '$130'],
    ['Double Ka Meetha', '$90', '$160'], ['Carrot Halwa', '$90', '$160'],
    ['Bread Halwa', '$90', '$160'], ['Shahi Tukda', '$100', '$180'],
    ['Kaddu Ka Kheer', '$100', '$180'],
  ],
  breads: [
    ['Paratta', '>30', '$2/ea'], ['Butter & Garlic Naan', '>30', '$2/ea'],
    ['Chapathi', '>30', '$2/ea'], ['Poori', '>30', '$2/ea'],
  ],
  addons: [
    { label: 'Plates & Cutlery', price: '$0.75 / person' },
    { label: 'Setup & Sternos', price: '$100 flat (booked in advance)' },
    { label: 'Delivery', price: 'Based on distance' },
  ],
};

/**
 * Hook to fetch catering data from Firestore (admin overrides) or fall back to defaults.
 * Returns { data, setData, loading, dirty, saving, publish, discard, source, loadError, fromCache }.
 * `enabled: false` reads nothing until it turns true (a dialog that needs the menu only sometimes).
 *
 * `source` says where `data` came from — the Catering Planner prices quotes from it, so
 * it must know when it is NOT looking at the published menu:
 *   'live'     the published menu (cateringConfig/main)
 *   'builtin'  nothing published yet — the built-in menu below IS the menu
 *   'fallback' the published menu couldn't be read (offline, no access) — showing the
 *              built-in menu instead; `loadError` holds the reason
 * `fromCache` = the published menu came from this device's saved copy (offline).
 */
export function useCateringData({ enabled = true } = {}) {
  const [data, setDataRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [source, setSource] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [fromCache, setFromCache] = useState(false);

  const applySnap = useCallback((snap) => {
    setFromCache(!!snap.metadata?.fromCache);
    if (snap.exists()) {
      const raw = snap.data();
      setDataRaw(raw._json ? JSON.parse(raw._json) : raw);
      setSource('live');
    } else {
      setDataRaw(DEFAULT_DATA);
      setSource('builtin');
    }
    setLoadError(null);
  }, []);

  const applyFailure = useCallback((err) => {
    setFromCache(false);
    setDataRaw(DEFAULT_DATA);
    setSource('fallback');
    setLoadError(err || new Error('Could not load the catering menu'));
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    getDoc(doc(db, 'cateringConfig', 'main'))
      .then((snap) => { if (!cancelled) applySnap(snap); })
      .catch((err) => { if (!cancelled) applyFailure(err); })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [applySnap, applyFailure, enabled]);

  const setData = useCallback((updater) => {
    setDataRaw((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      setDirty(true);
      return next;
    });
  }, []);

  const publish = useCallback(async (latestData) => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'cateringConfig', 'main'), { _json: JSON.stringify(latestData) });
      setDirty(false);
    } finally {
      setSaving(false);
    }
  }, []);

  const discard = useCallback(() => {
    setLoading(true);
    getDoc(doc(db, 'cateringConfig', 'main'))
      .then(applySnap)
      .catch(applyFailure)
      .finally(() => setLoading(false));
    setDirty(false);
  }, [applySnap, applyFailure]);

  return { data, setData, loading, dirty, saving, publish, discard, source, loadError, fromCache };
}

export { DEFAULT_DATA as DEFAULT_CATERING_DATA };
